import { makeStyles, Theme } from '@material-ui/core/styles';

// General, theme-aware, reusable utilities for Fleet screens — a single place,
// no screen-specific names. Components compose these classes (no inline CSS).
export const useStyles = makeStyles((theme: Theme) => ({
  // layout
  row: { display: 'flex', alignItems: 'center' },
  rowStart: { display: 'flex', alignItems: 'flex-start' },
  rowBaseline: { display: 'flex', alignItems: 'baseline' },
  wrap: { flexWrap: 'wrap' },
  justifyEnd: { justifyContent: 'flex-end' },
  pushRight: { marginLeft: 'auto' },
  minW0: { minWidth: 0 },
  gap8: { gap: 8 },
  gap10: { gap: 10 },
  gap12: { gap: 12 },
  gap13: { gap: 13 },
  gap14: { gap: 14 },
  gap18: { gap: 18 },
  gap24: { gap: 24 },
  gap26: { gap: 26 },
  sectionPad: { padding: '11px 16px' },
  footBar: {
    position: 'sticky',
    bottom: 0,
    background: '#fff',
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: '12px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: 18,
    flexWrap: 'wrap',
    boxShadow: '0 -6px 20px rgba(24,39,75,.07)',
    zIndex: 24
  },
  card: { background: '#fff', border: `1px solid ${theme.palette.divider}`, borderRadius: 10 },
  vDivider: { width: 1, height: 26, background: theme.palette.divider, flex: 'none' },

  // text
  mono: { fontFamily: 'ui-monospace,Menlo,monospace' },
  tabular: { fontVariantNumeric: 'tabular-nums' },
  ellipsis: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 },
  nowrap: { whiteSpace: 'nowrap' },
  right: { textAlign: 'right' },
  tracked: { letterSpacing: '0.05em' },
  upper: { textTransform: 'uppercase', letterSpacing: '0.05em' },
  wideUpper: { textTransform: 'uppercase', letterSpacing: '0.07em' },
  semibold: { fontWeight: 600 },
  bold: { fontWeight: 700 },
  extrabold: { fontWeight: 800 },
  muted: { color: theme.palette.grey[500] },
  faint: { color: theme.palette.grey[400] },
  primaryText: { color: theme.palette.primary.main },
  successText: { color: theme.palette.success.dark },
  dangerText: { color: theme.palette.error.dark },

  // reusable fragments
  pill: {
    display: 'inline-block',
    borderRadius: 6,
    padding: '1px 8px',
    background: theme.palette.grey[200]
  },
  pillSuccess: { color: theme.palette.success.dark, background: theme.palette.success.light },
  outlinedTag: {
    display: 'inline-block',
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 5,
    padding: '3px 10px'
  },
  autoGrid4: {
    display: 'grid',
    gridTemplateColumns: 'auto auto auto auto',
    gap: '2px 10px',
    alignItems: 'baseline'
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'auto 104px 104px 88px',
    gap: '2px 14px',
    alignItems: 'center'
  },

  mb10: { marginBottom: 10 },
  mb16: { marginBottom: 16 },
  field: { '&:first-of-type > label': { marginTop: 0 } },
  pointer: { cursor: 'pointer' },

  // soft info card (import dialog rows) + leading icon tile
  softCard: {
    background: theme.palette.grey[100],
    borderRadius: 10,
    padding: '14px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
    flexWrap: 'wrap'
  },
  iconTile: {
    width: 44,
    height: 52,
    borderRadius: 8,
    background: theme.palette.grey[200],
    display: 'grid',
    placeItems: 'center',
    flex: 'none'
  },

  // shared 13-column data grid (selection + reco + 9 data + diff + actions)
  gridRow: {
    display: 'grid',
    // Column widths come from the --alt-grid-cols var so a hidden column (e.g. Preț Net) can drop its track.
    gridTemplateColumns:
      'var(--alt-grid-cols, 38px 70px 136px minmax(120px,1fr) 96px 104px 92px 96px 116px 112px 62px 96px 104px 168px)',
    gap: 8,
    alignItems: 'center'
  },

  gridHead: {
    padding: '8px 16px',
    minHeight: 46,
    boxSizing: 'border-box',
    fontWeight: 800,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: theme.palette.grey[500],
    background: theme.palette.grey[100],
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    marginBottom: 10
  },
  // Order-detail variants header: sits flush inside the part card (no radius/margin/side gaps),
  // separated from the rows by a bottom divider instead of a floating margin.
  gridHeadFlush: {
    border: 0,
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRadius: 0,
    marginBottom: 0
  },
  sortLabel: {
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    '&:hover': { color: theme.palette.primary.main }
  },
  // Active-sort column label + the dedicated sort triangle, both in the brand colour so the
  // sorted column reads clearly.
  sortActive: {
    color: theme.palette.primary.main,
    fontWeight: 700
  },
  sortArrow: {
    display: 'inline-flex',
    alignItems: 'center',
    color: theme.palette.primary.main
  },
  filterBtn: {
    padding: 2,
    color: theme.palette.grey[400],
    '&:hover': { color: theme.palette.primary.main }
  },
  filterBtnOn: { color: theme.palette.primary.main },
  // Header cell layout matching the mockup `.hc`: label + sort arrow + filter funnel inline, 4px gap.
  // lineHeight 1 hugs the uppercase caps so the funnel (geometrically centered) aligns with the label.
  headCell: { display: 'flex', alignItems: 'center', gap: 4, minWidth: 0, lineHeight: 1 },
  // Fleet page background: the mockup page grey (--page: #EDEFF5).
  pageBg: { backgroundColor: '#EDEFF5' },

  // square icon button (reusable) + variants
  iconBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: `1px solid ${theme.palette.grey[300]}`,
    background: '#fff',
    display: 'grid',
    placeItems: 'center',
    color: theme.palette.grey[500],
    transition: '.15s',
    flex: 'none',
    padding: 0,
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      background: theme.palette.primary.extraLight
    },
    '&.Mui-disabled': {
      color: theme.palette.action.disabled,
      background: 'transparent',
      borderColor: 'transparent'
    }
  },
  iconBtnSm: { width: 28, height: 28 },
  iconBtnPrimary: {
    background: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: '#fff',
    '&:hover': {
      background: theme.palette.primary.dark,
      borderColor: theme.palette.primary.dark,
      color: '#fff'
    }
  },
  iconBtnDanger: {
    '&:hover': {
      borderColor: theme.palette.error.main,
      color: theme.palette.error.main,
      background: theme.palette.error.light
    }
  },
  iconBtnStar: {
    color: theme.palette.grey[300],
    '&:hover': {
      borderColor: theme.palette.warning.main,
      color: theme.palette.warning.main,
      background: theme.palette.warning.light
    },
    // Child star is locked: fade it so disabled reads clearly, idem CustomCheckbox.
    '&.Mui-disabled': { opacity: 0.5 }
  },
  iconBtnStarOn: {
    borderColor: theme.palette.warning.main,
    color: '#fff',
    background: theme.palette.warning.main,
    '&:hover': {
      color: '#fff',
      background: theme.palette.warning.main,
      borderColor: theme.palette.warning.main
    },
    // Disabled + on stays clearly filled (warning), just muted — idem checked checkbox.
    '&.Mui-disabled': {
      color: '#fff',
      background: theme.palette.warning.main,
      borderColor: theme.palette.warning.main,
      opacity: 0.5
    }
  },
  hidden: { visibility: 'hidden', pointerEvents: 'none' },

  // pill badges (reusable) + variants
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '2.5px 8px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: 'nowrap'
  },
  badgeNeutral: {
    background: theme.palette.grey[200],
    color: theme.palette.grey[500],
    border: `1px solid ${theme.palette.grey[300]}`
  },
  badgeOutline: {
    background: '#fff',
    color: theme.palette.grey[500],
    border: `1px solid ${theme.palette.grey[300]}`,
    fontWeight: 600
  },
  badgeBlue: { background: theme.palette.primary.extraLight, color: theme.palette.primary.dark },
  badgeGeneral: {
    background: theme.palette.chip.fill,
    color: theme.palette.text.primary,
    border: 'none',
    fontWeight: 600
  },

  // compact grid input/select
  fin: {
    height: 32,
    width: '100%',
    padding: '0 9px',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 7,
    background: '#fff',
    fontSize: 13,
    fontFamily: 'Montserrat',
    color: theme.palette.text.primary,
    boxSizing: 'border-box',
    '&:focus': {
      outline: 'none',
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 3px ${theme.palette.primary.extraLight}`
    }
  },

  // code input + inline copy action (mockup .codefield)
  codeField: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    minWidth: 0
  },

  // clickable code (copy)
  codeCopy: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    minWidth: 0,
    maxWidth: '100%',
    padding: '2px 4px',
    marginLeft: -4,
    border: 0,
    borderRadius: 6,
    color: theme.palette.text.primary,
    background: 'none',
    cursor: 'pointer',
    fontFamily: 'Montserrat',
    fontSize: 12.5,
    '& code': { color: 'inherit', fontFamily: 'inherit', fontSize: 'inherit' },
    '&:hover': { background: theme.palette.primary.extraLight, color: theme.palette.primary.dark }
  },

  // part card + rows
  groupCard: {
    background: '#fff',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    marginBottom: 10,
    '&$groupCardOpen': {
      borderColor: theme.palette.primary.light,
      boxShadow: '0 4px 16px rgba(91,124,250,.10)'
    }
  },
  groupCardOpen: {},
  // Clips inner content to the card radius so header + alternativeParts read as one block.
  groupCardFlush: {
    overflow: 'hidden'
  },
  groupHead: {
    padding: '16px 16px',
    cursor: 'pointer',
    transition: 'background .12s',
    '&:hover': { background: theme.palette.primary.extraLight },
    '$groupCardOpen &': { background: theme.palette.primary.extraLight }
  },
  chev: {
    width: 26,
    height: 26,
    borderRadius: 7,
    background: theme.palette.grey[200],
    display: 'grid',
    placeItems: 'center',
    color: theme.palette.grey[500],
    transition: '.18s',
    border: 0,
    cursor: 'pointer',
    '$groupCardOpen &': {
      background: theme.palette.primary.main,
      color: '#fff',
      transform: 'rotate(90deg)'
    }
  },
  selectedRow: {
    padding: '9px 16px',
    borderTop: `1px solid ${theme.palette.divider}`,
    background: theme.palette.grey[100]
  },
  codeRow: {
    padding: '9px 16px',
    borderTop: `1px dashed ${theme.palette.divider}`,
    background: theme.palette.grey[100]
  },
  resultsBody: { borderTop: `1px solid ${theme.palette.divider}` },
  // Capped rows scroll; the gutter is reserved only when a scrollbar is actually present (auto),
  // so short lists don't show an empty strip on the right.
  resultsScroll: { maxHeight: 250, overflowY: 'auto', scrollbarGutter: 'auto' },
  gutterReserve: { overflow: 'hidden' },
  resultRow: {
    padding: '9px 16px',
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
    cursor: 'pointer',
    transition: 'background .12s',
    background: '#fff',
    '&:hover': { background: theme.palette.grey[100] },
    '&$resultRowSel': {
      background: theme.palette.primary.extraLight,
      boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`
    },
    '&$resultRowReco': { background: theme.palette.warning.light }
  },
  resultRowSel: {},
  resultRowReco: {},
  selBar: {
    padding: '9px 16px',
    background: theme.palette.grey[100],
    color: theme.palette.grey[500],
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  emptyRow: { padding: '22px 16px', textAlign: 'center', color: theme.palette.grey[400] },
  listFoot: { padding: '14px 0 0' },

  // section header band (TOTAL COMANDA / ISTORIC COMANDA) — theme accent instead of grey
  sectionHeader: {
    padding: 12,
    fontWeight: 700,
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  },

  // full-screen dialog title bar
  titleBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '12px 20px',
    background: theme.palette.primary.main,
    color: '#fff'
  },
  titleBarTitle: { margin: 0 },
  titleBarSubtitle: { opacity: 0.7 },
  titleBarClose: { marginLeft: 'auto', color: '#fff' },

  // right-aligned single-button dialog footer (notify-support form)
  dialogFooter: { display: 'flex', justifyContent: 'flex-end', marginTop: 8 }
}));

export type StyleClasses = ReturnType<typeof useStyles>;
